import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCheckoutPaymentMethod, setInsufficientHouseChargeBalance, setScrollToShowNewAmount } from "redux/actions";
import "./payment-method.scss";
import { useParams } from "react-router-dom";
import useSmoothScroll from 'hooks/useSmoothScroll';

const PAYMENT_METHOD_CHANGE_SCROLL_TOP = false

const PaymentMethod = () => {
  const dispatch = useDispatch();
  const { checkoutType } = useParams();
  const isCateringMeal = checkoutType === "catering-meal"
  const isEcom = !isCateringMeal

  const { storeDetails } = useSelector(({ store }) => store);
  const { 
    checkoutStoreType, 
    ebtPaymentPaidList, 
    checkoutPickupType, 
    checkoutPaymentMethod, 
    scrollToShowNewAmount 
  } = useSelector(
    ({ checkout }) => checkout
  );

  const { cateringAndMealKitProductCartList, productCartList, productCartListLoading } = useSelector(
    ({ cartWishlist }) => cartWishlist
  );

  const isEbtProductAvailable = productCartList.some(
    (product) => product.ebt_item === "1"
  );

  const {
    pickup_cc = "0",
    pickup_cod = "0",
    delivery_cc = "0",
    delivery_cod = "0",
    catering_pickup_cc = "0",
    catering_pickup_cod = "0",
    catering_delivery_cc = "0",
    allow_mealkit_delivery_cc = "0",
    catering_delivery_cod = "0",
    allow_mealkit_delivery_cod = "0",
    allow_delivery_default_payment_method = "",
    allow_pickup_default_payment_method = "",
    allow_catering_pickup_default_payment_method = "0",
    allow_catering_delivery_default_payment_method = "0",
    allow_mealkit_default_pickup_payment_method = "0",
    allow_mealkit_default_delivery_payment_method = "0",
    allow_pickup_ebt = "0",
    allow_delivery_ebt = "0",
    allow_mealkit_pickup_cc = "0",
    allow_mealkit_pickup_cod = "0",
  } = storeDetails || {};
  
  const commonDetailsData = useSelector(({ common }) => common.commonDetailsData);
  const allowHouseCharge = commonDetailsData?.header_section?.allow_house_charge === "1"
  let {
    allow_ecom_pickup_house_charge = "0",
    allow_ecom_delivery_house_charge = "0",

    allow_catering_pickup_house_charge = "0",
    allow_catering_delivery_house_charge = "0",
    allow_mealkit_delivery_house_charge = "0",

    allow_mealkit_asap_pickup_house_charge = "0",
    allow_mealkit_schedule_pickup_house_charge = "0",
  } = storeDetails || {};

  if (!allowHouseCharge) {
    allow_ecom_pickup_house_charge = "0"
    allow_ecom_delivery_house_charge = "0"

    allow_catering_pickup_house_charge = "0"
    allow_catering_delivery_house_charge = "0"
    allow_mealkit_delivery_house_charge = "0"

    allow_mealkit_asap_pickup_house_charge = "0"
    allow_mealkit_schedule_pickup_house_charge = "0"
  }

  const { is_mealkit = "0" } = cateringAndMealKitProductCartList?.[0] || {};

  const { cartAmountDetails, isFollowedEbtPayment, cateringAndMealkitCartAmountDetails } = useSelector(({ checkout }) => checkout)
  const getEbtLabel = () => {
    if (checkoutStoreType === "delivery") {
      return storeDetails?.delivery_ebt_lable||'Pay with SNAP EBT'
    }
    return storeDetails?.pickup_ebt_lable||'Pay with SNAP EBT'
  }


  useEffect(() => {
    // auto select cc in case of ebt split payment
    if (isFollowedEbtPayment && ebtPaymentPaidList.length) {
      dispatch(selectCheckoutPaymentMethod("cc"))
    }
  }, [dispatch, ebtPaymentPaidList.length, isFollowedEbtPayment, checkoutPaymentMethod])

  const { data: { price_list = [] } = {} } =
    cartAmountDetails || {};

  const houseChargeAmount =  (isCateringMeal ? +cateringAndMealkitCartAmountDetails?.house_charge_amount : +cartAmountDetails?.house_charge_amount) || 0
  useEffect(() => {
    const itemPriceList = isCateringMeal ? cateringAndMealkitCartAmountDetails?.data?.price_list : price_list
    const orderAmount = +itemPriceList?.find(list => list.key_name === "grand_total")?.amount || 0;
    const insufficientHouseChargeBalance = orderAmount > houseChargeAmount;
    dispatch(setInsufficientHouseChargeBalance(insufficientHouseChargeBalance));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartAmountDetails, cateringAndMealkitCartAmountDetails, dispatch]);

  useEffect(() => {
    let defaultPaymentMethod
    if (isCateringMeal) {
      if (is_mealkit === "1") { // mealkit
        if (isStorePickup) {
          defaultPaymentMethod = allow_mealkit_default_pickup_payment_method;
        } else if (isDelivery) {
          defaultPaymentMethod = allow_mealkit_default_delivery_payment_method;
        }
      } else if (is_mealkit === "2") { // catering
        if (isStorePickup) {
          defaultPaymentMethod = allow_catering_pickup_default_payment_method;
        } else if (isDelivery) {
          defaultPaymentMethod = allow_catering_delivery_default_payment_method;
        }
      }
    } else {
      // grocery
      const isEbtPay = isEbtProductAvailable && ebtPaymentPaidList.length === 0
      if (isStorePickup) {
        defaultPaymentMethod = isEbtPay ? "ebt" : allow_pickup_default_payment_method;
      }
      if (isDelivery) {
        defaultPaymentMethod = isEbtPay ? "ebt" : allow_delivery_default_payment_method;
      }
    }

    if (defaultPaymentMethod) {
      dispatch(selectCheckoutPaymentMethod(defaultPaymentMethod));
    }
  }, [checkoutStoreType, storeDetails, productCartListLoading]); // eslint-disable-line

  const scrollToElement = useSmoothScroll();
  useEffect(() => {
    if (!PAYMENT_METHOD_CHANGE_SCROLL_TOP) {
      return
    }
    
    if (scrollToShowNewAmount) {
      dispatch(setScrollToShowNewAmount(false));
      setTimeout(() => {
        scrollToElement("checkout-details-section", 600);
      }, 500);
    }
  }, [dispatch, scrollToElement, scrollToShowNewAmount]);

  const handleChange = (e) => {
    const { value } = e.target;
    dispatch(selectCheckoutPaymentMethod(value));
  };

  const authorizedAmountObj =
    price_list &&
    price_list.find(
      (list) =>
        list.key_name ===
        (isFollowedEbtPayment
          ? "total_final_payable_amount"
          : "authorized_amount")
    );

  const isStorePickup = checkoutStoreType === "store_pickup"
  const isDelivery = checkoutStoreType === "delivery"
  const isCatering = is_mealkit === "2"
  const isCateringMealkitDelivery = isCateringMeal && isDelivery

  const deliveryCCEnabled = isCatering ? catering_delivery_cc : allow_mealkit_delivery_cc
  const deliveryCODEnabled = isCatering ? catering_delivery_cod : allow_mealkit_delivery_cod
  const deliveryHouseChargeEnabled = isCatering ? allow_catering_delivery_house_charge : allow_mealkit_delivery_house_charge

  const showCateringMealkitDeliveryCC = isCateringMealkitDelivery && deliveryCCEnabled === "1"
  const showCateringMealkitDeliveryCOD = isCateringMealkitDelivery && deliveryCODEnabled === "1"
  const showCateringMealkitDeliveryHouseCharge = isCateringMealkitDelivery && deliveryHouseChargeEnabled === "1"

  const isCateringPickup = isCateringMeal && isCatering && isStorePickup

  const houseChargeProps = {
    handleChange,
    checkoutPaymentMethod,
    houseChargeAmount
  }

  const creditCardProps = {
    handleChange,
    checkoutPaymentMethod
  }

  return (
    <div className="payment-method-section">
      <h1>
        <strong>Choose payment method</strong>
      </h1>
      {isEbtProductAvailable && ebtPaymentPaidList.length === 0 && (
        <div className="radio-alignment">
          {/* grocery */}
          {isEcom &&
            (allow_delivery_ebt === "1" || allow_pickup_ebt === "1") && (
              <div>
                <input
                  type="radio"
                  value="ebt"
                  id="ebt"
                  name="paymentMethod"
                  onChange={handleChange}
                  checked={checkoutPaymentMethod === "ebt"}
                />
                <label htmlFor="ebt">{getEbtLabel()}</label>
              </div>
            )}
        </div>
      )}

      {/* grocery */}
      {isEcom && (
        <div className="radio-alignment">
          {((isStorePickup && pickup_cc === "1") ||
            (isDelivery && delivery_cc === "1")) && (
              <div style={{ display: "flex" }}>
                <CreditCard {...creditCardProps} />
                {ebtPaymentPaidList && !!ebtPaymentPaidList.length ? (
                  <div
                    className="balance-to-pay"
                    style={{ color: "var(--bg-danger)", marginLeft: "1rem" }}
                  >
                    (Balance to pay : $
                    {authorizedAmountObj && authorizedAmountObj.amount})
                  </div>
                ) : (
                  ""
                )}
              </div>
            )}
        </div>
      )}

      {/* grocery */}
      {isEcom && ebtPaymentPaidList.length === 0 && (
        <>
          {/* Checkout Page COD Label added Dynamically - Card Num : 1634 - 07/02/2023  - Madhu */}
          {(isStorePickup && pickup_cod === "1") || 
           (isDelivery && delivery_cod === "1") ? (
             <div className="radio-alignment">
              <div>
                <input
                  type="radio"
                  value="cod"
                  id="COD"
                  name="paymentMethod"
                  onChange={handleChange}
                  checked={checkoutPaymentMethod === "cod"}
                />
                <label htmlFor="COD">{isStorePickup ? storeDetails.pickup_cod_lable : storeDetails.delivery_cod_lable}</label>
                </div>
            </div>
          ) : null}
        <div className="radio-alignment  housecharge">
          {(isStorePickup && allow_ecom_pickup_house_charge === "1") || 
           (isDelivery && allow_ecom_delivery_house_charge === "1") ? (
            <HouseCharge {...houseChargeProps} />
          ) : null}
        </div>
        </>
      )}

      {/* mealkit */}
      {isCateringMeal && is_mealkit === "1" && isStorePickup && (
        <>
          {allow_mealkit_pickup_cc === "1" && (
            <div className="radio-alignment">
              <CreditCard {...creditCardProps} />
            </div>
          )}
          {allow_mealkit_pickup_cod === "1" && (
            <div className="radio-alignment">
              <div>
                <input
                  type="radio"
                  value="cod"
                  id="COD"
                  name="paymentMethod"
                  onChange={handleChange}
                  checked={checkoutPaymentMethod === "cod"}
                />
                <label htmlFor="COD">{storeDetails.pickup_cod_lable}</label>
              </div>
            </div>
          )}
          {(allow_mealkit_asap_pickup_house_charge === "1" && checkoutPickupType === "asap") ||
           (allow_mealkit_schedule_pickup_house_charge === "1" && checkoutPickupType === "later")
            ? <HouseCharge {...houseChargeProps} />
            : null
          }
        </>
      )}

      {/* catering pickup*/}
      {isCateringPickup && (
          <div className="radio-alignment">
            {catering_pickup_cc === "1" && (
              <CreditCard {...creditCardProps} />
            )}
          </div>
        )}
      {isCateringPickup && (
        <>
          <div className="radio-alignment">
            {catering_pickup_cod === "1" && (
              <div>
                <input
                  type="radio"
                  value="cod"
                  id="COD"
                  name="paymentMethod"
                  onChange={handleChange}
                  checked={checkoutPaymentMethod === "cod"}
                />
                <label htmlFor="COD">{storeDetails.pickup_cod_lable}</label>
              </div>
            )}
          </div>
          <div className="radio-alignment">
            {allow_catering_pickup_house_charge === "1" && (
              <HouseCharge {...houseChargeProps} />
            )}
          </div>
        </>
        )}

      {/* catering/mealkit delivery*/}
      {showCateringMealkitDeliveryCC
         ? (<div className="radio-alignment">
            <CreditCard {...creditCardProps} />
          </div>)
         : null}

      {showCateringMealkitDeliveryCOD
      ? (
          <div className="radio-alignment">
              <div>
                <input
                  type="radio"
                  value="cod"
                  id="COD"
                  name="paymentMethod"
                  onChange={handleChange}
                  checked={checkoutPaymentMethod === "cod"}
                />
                <label htmlFor="COD">{storeDetails.delivery_cod_lable}</label>
              </div>
          </div>
        ) : null}

        {showCateringMealkitDeliveryHouseCharge ? (
          <div className="radio-alignment">
              <HouseCharge {...houseChargeProps} />
          </div>
        ) : null}
      
    </div>
  );
};

function HouseCharge({handleChange, checkoutPaymentMethod, houseChargeAmount}) {
  const { insufficientHouseChargeBalance } = useSelector(({ checkout }) => checkout);
  return (<><div>
    <input
      type="radio"
      value="house_charge"
      id="house_charge"
      name="paymentMethod"
      onChange={handleChange}
      checked={checkoutPaymentMethod === "house_charge"}
    />
    <label htmlFor="house_charge">House Charge </label>
    <div className="item-value">(Balance: ${houseChargeAmount})</div>
  </div>
  {insufficientHouseChargeBalance && <div className="error-text" style={{ left: "27px" }}>Insufficient house charge balance</div>}
  </>
  )
}

function CreditCard({handleChange, checkoutPaymentMethod}) {
  return (
    <div>
      <input
        type="radio"
        value="cc"
        id="credit-card"
        name="paymentMethod"
        onChange={handleChange}
        checked={checkoutPaymentMethod === "cc"}
      />
      <label htmlFor="credit-card">Credit Card</label>
    </div>
  )
}

export default PaymentMethod;
